/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useState } from 'react';
import TextToSpeech from '../components/elevenlabs/TextToSpeech';
import VoiceCloning from '../components/elevenlabs/VoiceCloning';
import { useElevenLabsContext } from '../contexts/ElevenLabsContext';
import './ElevenLabsDemo.scss';

const ElevenLabsDemo: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'tts' | 'cloning'>('tts');
  const [apiKey, setApiKey] = useState('');
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  
  const { service, initializeService, isLoading, voices } = useElevenLabsContext();

  const handleApiKeySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (apiKey.trim()) {
      initializeService(apiKey);
      setShowApiKeyInput(false);
    }
  };

  const handleVoiceCreated = (voiceId: string) => {
    console.log('Voice created with ID:', voiceId);
    setActiveTab('tts'); // Switch to TTS tab after creating voice
  };

  if (!service) {
    return (
      <div className="elevenlabs-demo">
        <div className="elevenlabs-demo__setup">
          <h2>Eleven Labs Integration</h2>
          <p>
            This demo showcases Eleven Labs text-to-speech and voice cloning capabilities.
            To get started, please enter your Eleven Labs API key.
          </p>
          
          {!showApiKeyInput ? (
            <button
              onClick={() => setShowApiKeyInput(true)}
              className="elevenlabs-demo__setup-button"
            >
              Configure Eleven Labs
            </button>
          ) : (
            <form onSubmit={handleApiKeySubmit} className="elevenlabs-demo__api-form">
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter your Eleven Labs API key"
                className="elevenlabs-demo__api-input"
                required
              />
              <div className="elevenlabs-demo__api-actions">
                <button
                  type="submit"
                  className="elevenlabs-demo__api-submit"
                  disabled={!apiKey.trim()}
                >
                  Connect
                </button>
                <button
                  type="button"
                  onClick={() => setShowApiKeyInput(false)}
                  className="elevenlabs-demo__api-cancel"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
          
          <div className="elevenlabs-demo__info">
            <h4>Don't have an API key?</h4>
            <p>
              Get your free API key at{' '}
              <a href="https://elevenlabs.io/" target="_blank" rel="noopener noreferrer">
                elevenlabs.io
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="elevenlabs-demo">
      <div className="elevenlabs-demo__header">
        <h2>Eleven Labs Voice Studio</h2>
        <div className="elevenlabs-demo__tabs">
          <button
            className={`elevenlabs-demo__tab ${
              activeTab === 'tts' ? 'elevenlabs-demo__tab--active' : ''
            }`}
            onClick={() => setActiveTab('tts')}
          >
            Text to Speech
          </button>
          <button
            className={`elevenlabs-demo__tab ${
              activeTab === 'cloning' ? 'elevenlabs-demo__tab--active' : ''
            }`}
            onClick={() => setActiveTab('cloning')}
          >
            Voice Cloning
          </button>
        </div>
      </div>

      <div className="elevenlabs-demo__content">
        {activeTab === 'tts' && (
          <div className="elevenlabs-demo__section">
            <h3>Text to Speech</h3>
            <p>Convert text to natural-sounding speech using various voice options.</p>
            <TextToSpeech />
          </div>
        )}

        {activeTab === 'cloning' && (
          <div className="elevenlabs-demo__section">
            <h3>Voice Cloning</h3>
            <p>Create a custom voice by uploading audio samples.</p>
            <VoiceCloning onVoiceCreated={handleVoiceCreated} />
          </div>
        )}
      </div>

      <div className="elevenlabs-demo__status">
        <div className="elevenlabs-demo__status-item">
          <strong>Status:</strong> {isLoading ? 'Loading...' : 'Ready'}
        </div>
        <div className="elevenlabs-demo__status-item">
          <strong>Available Voices:</strong> {voices.length}
        </div>
      </div>
    </div>
  );
};

export default ElevenLabsDemo;
